import { ReactElement } from 'react';
import remCalc from '@spa/lib/remCalc';

interface ItemProperties {
  readonly width: number;
  readonly gap: number;
}

interface HeightAndWidthPerBreakpointEntry {
  readonly minViewportWidth: number;
  readonly height: number;
  readonly maxWidth: number;
  readonly itemProperties?: ItemProperties;
}

const getPlaceholderStyle = (
  classIdentifier: string,
  itemWidth: number,
  itemsToShow: number
): string => {
  let newStyleChunk: string = `${classIdentifier} {display: none;}`;
  for (let itemIdx = 0; itemIdx < itemsToShow; itemIdx++) {
    const leftStyle: string = `${classIdentifier}:nth-child(${
      itemIdx + 1
    }) {display: block; left: ${remCalc(itemWidth * itemIdx)}rem !important;}`;
    newStyleChunk += ` ${leftStyle}`;
  }
  return newStyleChunk;
};

const getStyleAttributes = (
  cssId: string,
  className: string,
  { height, itemProperties, maxWidth }: HeightAndWidthPerBreakpointEntry,
  placeholderLeft: boolean
): string => {
  const classIdentifier: string = `[id^="${cssId}"].${className}`;
  let newStyleChunk: string = `${classIdentifier} {height: ${remCalc(height)}rem;`;

  if (itemProperties) {
    const { gap, width: itemWidth }: ItemProperties = itemProperties;

    newStyleChunk += ` width: ${remCalc(itemWidth - gap)}rem;}`;

    if (placeholderLeft) {
      const itemsToShow: number = Math.ceil(maxWidth / itemWidth);

      newStyleChunk += ` ${getPlaceholderStyle(classIdentifier, itemWidth, itemsToShow)}`;
    }
  } else {
    newStyleChunk += ` max-width: ${remCalc(maxWidth)}rem;}`;
  }

  return newStyleChunk;
};

const calculateStyleChunks = (
  cssId: string,
  sortedHeightAndWidthPerBreakpointEntry: Array<HeightAndWidthPerBreakpointEntry>,
  className: string,
  placeholderLeft: boolean
): string => {
  const mediaQueries: Array<string> = [];
  const containerQueries: Array<string> = [];

  sortedHeightAndWidthPerBreakpointEntry.forEach((entry: HeightAndWidthPerBreakpointEntry) => {
    const minViewportWidth: number = entry.minViewportWidth;
    const isQuery: boolean = minViewportWidth !== 0;
    const styleAttributes: string = getStyleAttributes(cssId, className, entry, placeholderLeft);

    if (isQuery) {
      const mediaQuery: string = `@media (min-width: ${minViewportWidth}px) { ${styleAttributes} }`;
      const containerQuery: string = `@container (min-width: ${minViewportWidth}px) { ${styleAttributes} }`;
      mediaQueries.push(mediaQuery);
      containerQueries.push(containerQuery);
    } else {
      mediaQueries.push(styleAttributes);
    }
  });

  return mediaQueries.concat(containerQueries).join(' ');
};

export const generateStyleTag = (
  cssId: string,
  heightAndWidthPerBreakpointEntries: Array<HeightAndWidthPerBreakpointEntry>,
  className: string,
  placeholderLeft: boolean = false
): ReactElement => {
  const sortedHeightAndWidthPerBreakpointEntries: Array<HeightAndWidthPerBreakpointEntry> = [
    ...heightAndWidthPerBreakpointEntries
  ].sort((entry1: HeightAndWidthPerBreakpointEntry, entry2: HeightAndWidthPerBreakpointEntry) => {
    return entry1.minViewportWidth - entry2.minViewportWidth;
  });

  const styleChunksCombined: string = calculateStyleChunks(
    cssId,
    sortedHeightAndWidthPerBreakpointEntries,
    className,
    placeholderLeft
  );

  return <style>{styleChunksCombined}</style>;
};
